import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  IconButton,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";
import GenericSelectInput from "src/components/input-components/GenericSelectInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";

export interface IMediaTextV1Widget {
  type: string;
  Id: string;
  Title: string;
  Description: string;
  Hint: string;
  Images: string[];
  Link: string;
  LinkTitle: string;
}

interface MediaTextV1ItemProps {
  item: IMediaTextV1Widget;
  error: any;
  onChange: (updatedItem: IMediaTextV1Widget, field: string) => void;
  onblur: (field: string) => void;
}

const MediaTextV1Item: React.FC<MediaTextV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Extract fields with defaults
  // const id: string = typeof item.Id === "string" ? item.Id : "";
  const title: string = typeof item.Title === "string" ? item.Title : "";
  const description: string =
    typeof item.Description === "string" ? item.Description : "";
  const hint: string = typeof item.Hint === "string" ? item.Hint : "right";
  const images: string[] = Array.isArray(item.Images) ? item.Images : [];
  const link: string = typeof item.Link === "string" ? item.Link : "";
  const linkTitle: string = typeof item.LinkTitle === "string" ? item.LinkTitle : "";

  // Helper: extract value from event or use raw value
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handlers for fields
  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Id: getValue(e) }, "Id");
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Description: getValue(e) }, "Description");
  };

  const handleHintChange = (value: string) => {
    onChange({ ...item, Hint: value }, "Hint");
  };

  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Link: getValue(e) }, "Link");
  };

  const handleLinkTitleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, LinkTitle: getValue(e) }, "LinkTitle");
  };

  // Image Picker for multiple images
  const [imagePickerOpen, setImagePickerOpen] = useState(false);

  const handleOpenImagePicker = () => {
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selected: string | string[]) => {
    const newImages = Array.isArray(selected) ? selected : [selected];
    onChange({ ...item, Images: newImages }, "Images");
    setImagePickerOpen(false);
  };

  const handleRemoveImage = (indexToRemove: number) => {
    const updatedImages = images.filter((_, i) => i !== indexToRemove);
    onChange({ ...item, Images: updatedImages }, "Images");
  };

  const hintOptions = {
    left: "Left",
    right: "Right",
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Media Text Widget
        </Typography>

        {/* ID Field */}
        {/* <GenericInput
          type="text"
          name="Id"
          title="Section ID"
          value={id}
          error={error?.Id || ""}
          onChange={(e) => handleIdChange(e)}
          onBlur={() => onblur("Id")}
          disabled={false}
        /> */}

        {/* Title Field */}
        <GenericInput
          type="text"
          name="Title"
          title="Title"
          value={title}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />

        {/* Description Field - Using TextArea for HTML content */}
        <GenericTextAreaInput
          name="Description"
          title="Description (HTML)"
          value={description}
          error={error?.Description || ""}
          onChange={(e) => handleDescriptionChange(e)}
          onBlur={() => onblur("Description")}
          disabled={false}
        />

        {/* Hint Field - Dropdown for positioning */}
        <GenericSelectInput
          name="Hint"
          title="Media Position"
          value={hint}
          error={error?.Hint || ""}
          onChange={(value) => handleHintChange(value)}
          onBlur={() => onblur("Hint")}
          disabled={false}
          items={hintOptions}
          type=""
        />

        {/* Link Title */}
        <GenericInput
          type="text"
          name="LinkTitle"
          title="Link Title"
          value={linkTitle}
          error={error?.LinkTitle || ""}
          onChange={(e) => handleLinkTitleChange(e)}
          onBlur={() => onblur("LinkTitle")}
          disabled={false}
        />

        {/* Link */}
        <GenericInput
          type="text"
          name="Link"
          title="Link URL"
          value={link}
          error={error?.Link || ""}
          onChange={(e) => handleLinkChange(e)}
          onBlur={() => onblur("Link")}
          disabled={false}
        />

        {/* Images Section */}
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          Images
        </Typography>

        {/* Display selected images */}
        {images.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {images.map((imageUrl, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    position: "relative",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    overflow: "hidden",
                    height: 150,
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={`Media ${index + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <IconButton
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    }}
                    onClick={() => handleRemoveImage(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Button to open image picker */}
        <Button variant="outlined" onClick={handleOpenImagePicker}>
          {images.length > 0 ? "Update Images" : "Select Images"}
        </Button>

        {error?.Images && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error.Images}
          </Typography>
        )}
      </CardContent>

      {/* Image Picker Dialog */}
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={true}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Card>
  );
};

export default MediaTextV1Item;